import { Dispatch, createSlice } from '@reduxjs/toolkit'
import { DateRange } from '../../types'
import {
  endOfDay,
  endOfMinute,
  endOfMonth,
  endOfWeek,
  isValid,
  startOfDay,
  startOfMinute,
  startOfMonth,
  startOfWeek,
  subHours,
  subMinutes
} from 'date-fns'
import TimePeriod from '../../enums/TimePeriod'
import { formatISO } from '../../utils/date'

interface QueryState {
  /**
   * @deprecated
   */
  dateRange: DateRange

  from: Date

  to: Date

  timePeriod: TimePeriod

  /**
   * Indicates if tracking the current time automatically.
   */
  autoRefresh: boolean
}

const initialState: QueryState | undefined = getInitialState()

const querySlice = createSlice({
  name: 'query',
  initialState,
  reducers: {
    setDateRangeState(state, action) {
      state.dateRange = action.payload
      state.from = action.payload.from
      state.to = action.payload.to
    },
    setFromState(state, action) {
      state.dateRange = {
        from: action.payload,
        to: state.dateRange.to
      }
      state.from = action.payload
    },
    setToState(state, action) {
      state.dateRange = {
        from: state.dateRange.from,
        to: action.payload
      }
      state.to = action.payload
    },
    setTimePeriodState(state, action) {
      state.timePeriod = action.payload
    },
    setAutoRefresh(state, action) {
      state.autoRefresh = action.payload
    }
  }
})

function getInitialState() {
  const searchParams = new URLSearchParams(window.location.search)
  const foundDateRange = searchParams.get('daterange') ?? undefined
  const foundTimePeriod = searchParams.get('timeperiod') ?? undefined

  if (foundDateRange) {
    const dateRangeFromQuery = JSON.parse(foundDateRange)

    const from = startOfMinute(new Date(dateRangeFromQuery.from))
    const to = endOfMinute(new Date(dateRangeFromQuery.to))

    const dateRange: DateRange = {
      from,
      to
    }

    const timePeriod = (foundTimePeriod as TimePeriod) ?? TimePeriod.CUSTOM

    const relativeTimePeriods = [
      TimePeriod.ONE_HOUR,
      TimePeriod.TWENTYFOUR_HOURS,
      TimePeriod.THREE_DAYS,
      TimePeriod.SEVEN_DAYS,
      TimePeriod.THIRTY_DAYS
    ]

    const autoRefresh = relativeTimePeriods.includes(timePeriod)

    searchParams.delete('daterange')
    searchParams.set('from', formatISO(from))
    searchParams.set('to', formatISO(to))
    updateURLSearchParams(searchParams)

    return {
      dateRange,
      from,
      to,
      timePeriod,
      autoRefresh
    }
  }

  const now = new Date()

  let from = startOfMinute(subHours(now, 24))
  let to = endOfMinute(subMinutes(now, 1))

  const searchFrom = searchParams.get('from')
  if (searchFrom && isValid(new Date(searchFrom))) {
    from = new Date(searchFrom)
  }

  const searchTo = searchParams.get('to')
  if (searchTo && isValid(new Date(searchTo))) {
    to = new Date(searchTo)
  }

  if (!searchFrom && !searchTo) {
    switch (foundTimePeriod) {
      case TimePeriod.DAY:
        from = startOfDay(to)
        to = endOfDay(to)
        break
      case TimePeriod.WEEK:
        from = startOfWeek(to)
        to = endOfWeek(to)
        break
      case TimePeriod.MONTH:
        from = startOfMonth(to)
        to = endOfMonth(to)
        break
    }
  }

  const dateRange: DateRange = {
    from,
    to
  }

  // const foundHash: string = location.hash.substr(1)
  // const hash = foundHash ? '#' + foundHash : ''
  // searchParams.set('daterange', JSON.stringify(dateRange))

  // const newRelativePathQuery = window.location.pathname + hash + '?' + searchParams.toString()

  // window.history.replaceState(null, '', newRelativePathQuery)

  return {
    dateRange,
    from,
    to,
    timePeriod: TimePeriod.TWENTYFOUR_HOURS,
    autoRefresh: true
  }
}

export function getInitialTimePeriod() {
  return async (dispatch: Dispatch) => {
    const searchParams = new URLSearchParams(window.location.search)
    const foundTimePeriod = searchParams.get('timeperiod') ?? undefined
    if (foundTimePeriod) {
      dispatch(querySlice.actions.setTimePeriodState(foundTimePeriod))
    } else {
      searchParams.set('timeperiod', TimePeriod.TWENTYFOUR_HOURS)
      updateURLSearchParams(searchParams)
    }
  }
}

export function setDateRange(dateRange: DateRange) {
  return async (dispatch: Dispatch) => {
    const searchParams = new URLSearchParams(window.location.search)

    dispatch(querySlice.actions.setDateRangeState(dateRange))
    searchParams.set('from', formatISO(dateRange.from))
    searchParams.set('to', formatISO(dateRange.to))

    updateURLSearchParams(searchParams)
  }
}

export function setTimePeriod(timePeriod: TimePeriod) {
  return async (dispatch: Dispatch) => {
    const searchParams = new URLSearchParams(window.location.search)
    dispatch(querySlice.actions.setTimePeriodState(timePeriod))

    // Disable auto-refresh for custom and absolute time periods.
    if (
      timePeriod === TimePeriod.CUSTOM ||
      timePeriod === TimePeriod.DAY ||
      timePeriod === TimePeriod.WEEK ||
      timePeriod === TimePeriod.MONTH
    ) {
      dispatch(querySlice.actions.setAutoRefresh(false))
    }

    searchParams.set('timeperiod', timePeriod)
    updateURLSearchParams(searchParams)
  }
}

export function setAutoRefresh(autoRefresh: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(querySlice.actions.setAutoRefresh(autoRefresh))

    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('autorefresh', JSON.stringify(autoRefresh))
    updateURLSearchParams(searchParams)
  }
}

function updateURLSearchParams(searchParams: URLSearchParams) {
  const url = new URL(window.location.href)

  url.search = searchParams.toString()

  window.history.replaceState(null, '', url.toString())
}

export default querySlice.reducer
